import { useAuthContext } from 'contexts/AuthContext';
import FHIRPoster from 'data/FHIRPoster';
import { ObservationBuilder } from 'fhir-sdoh';
import moment from 'moment';
import React from 'react';
import { Form, Icon, Label, Loader, Popup } from 'semantic-ui-react';
import type { ResourceEntry } from 'utils/bundleNarrowing';
import { softDeleteObservation } from 'utils/softDeleteObservation';

import type { FlatVital } from '../../../types/flat-fhir';
import * as FHIRUtils from '../../../utils/FHIRUtils';

import type { ProfileChildBaseProps, ProfileMutate, ProfileObservationEntry, RejectionWithMessage, SubmittedForm } from './types';

/**
 * `patientId` and `mutate` are required here rather than optional, unlike the
 * rest of {@link ProfileChildBaseProps}. `handleDelete` and `handleSubmit` pass
 * `patientId` straight into `softDeleteObservation`/`ObservationBuilder`, which
 * declare it `string`, and both call `props.mutate().then(...)` unguarded — so
 * an omission of either is already a runtime failure, not a supported call.
 * `Profile` supplies both at the only render site.
 *
 * SUSPECTED DEFECT — the value `Profile` forwards for `patientId` originates as
 * `fhirPatient.id` in `components/patient/index.tsx`, and FHIR gives
 * `Patient.id` cardinality `0..1`. An id-less Patient resource therefore reaches
 * `softDeleteObservation`, which throws on a missing Provenance subject rather
 * than being caught by the `if (!resource)` guard above. Reported, not fixed:
 * defaulting the id would write a Provenance pointing at the wrong subject.
 */
type VitalsProps = Omit<ProfileChildBaseProps, 'patientId'> & {
  /** The member's FHIR `Patient.id`; see the defect note above. */
  patientId: string;
  /**
   * The vitals-coded Observation entries from `Profile.getDataByCode`. Absent
   * until the parent's profile query resolves, which is what the `if
   * (fhirVitals)` at the foot of this component branches on — an absent array
   * renders nothing rather than an empty list.
   */
  vitalsData?: ProfileObservationEntry[];
  /** Revalidates the profile query after a vital is created or soft-deleted. */
  mutate: ProfileMutate;
};

function Vitals(props: VitalsProps) {
  const authInfo = useAuthContext();

  const [loading, setLoading] = React.useState(!props.vitalsData ? 'Loading Vitals Data...' : '');

  const [popOpen, setpopOpen] = React.useState(false);
  const handlePopOpen = () => {
    setpopOpen(true);
  };
  const handleClose = () => {
    setpopOpen(false);
  };

  const handleDelete = (observationId: string) => {
    setLoading('Deleting Vital Label...');
    setpopOpen(false);
    const resource = props.vitalsData?.find((entry: ResourceEntry<'Observation'>) => entry?.resource?.id === observationId)?.resource;
    if (!resource) {
      setLoading('Error - Unable to delete Vital label: observation not found');
      return;
    }
    softDeleteObservation({
      observation: resource,
      token: authInfo.token,
      patientId: props.patientId,
      practitionerId: authInfo.practitionerId,
      practitionerName: authInfo.practitionerName,
      org: props.org,
    }).then(
      () => {
        setLoading('Deletion Success. Updating Vital UI...');
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; awaiting it would order this render behind a refetch.
        props.mutate().then(() => {
          setLoading('');
        });
      },
      (error: RejectionWithMessage) => {
        setLoading(`Error - Unable to delete Vital label: ${error.message}`);
      }
    );
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setpopOpen(false);

    const value = (e.target as SubmittedForm).elements.newobs.value;
    if (!value || value === '') {
      return false;
    }
    setLoading('Submitting Vital Label...');

    const body: fhir4.Observation = ObservationBuilder.buildVitalsObservation(
      props.patientId,
      new Date().toJSON(),
      '55284-4',
      'Blood pressure systolic and diastolic',
      value,
      authInfo.practitionerName
    );

    FHIRPoster(`/Observation/`, authInfo.token, body).then(
      () => {
        setLoading('Submission Success. Updating Vital UI...');
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; awaiting it would order this render behind a refetch.
        props.mutate().then(() => {
          setLoading('');
        });
      },
      (error: RejectionWithMessage) => {
        setLoading(`Error - Unable to submit Vital label: ${error.message}`);
      }
    );
  };

  const fhirVitals = props.vitalsData;
  let flatVitalsUnsorted: FlatVital[] = [];

  if (fhirVitals) {
    flatVitalsUnsorted = fhirVitals
      .filter((fhirVital: ResourceEntry<'Observation'>) => fhirVital?.resource?.status !== 'entered-in-error')
      .map((fhirVital: ResourceEntry<'Observation'>) => {
        return FHIRUtils.parseFHIRVital(fhirVital.resource);
      });
    let flatVitals: FlatVital[] = [];
    if (flatVitalsUnsorted) {
      flatVitals = flatVitalsUnsorted.sort((c1, c2) => {
        return moment(c2.effectiveDate, 'YYYY-MM-DD').valueOf() - moment(c1.effectiveDate, 'YYYY-MM-DD').valueOf();
      });
    }

    return (
      <>
        {loading.length > 0 && <Loader active inline="centered" content={loading} />}
        {flatVitals.map((flatVital, _i) => {
          const effectiveDate = moment(flatVital.effectiveDate, 'YYYY-MM-DD').format('MMMM Do, YYYY');
          let heightInFtAndInch: string | undefined = undefined;
          //show height observation in ft and inch when the data is stored in inch
          if (flatVital?.code === '8302-2' && flatVital?.unit === 'inches' && flatVital?.quantity) {
            // `FlatObservation.quantity` is `string`; both operators coerce it, which
            // is what produces the feet/inches figures today.
            const feet = Math.floor((flatVital.quantity as unknown as number) / 12);
            const remainingInches = (flatVital.quantity as unknown as number) % 12;
            heightInFtAndInch = `${feet} Feet ${remainingInches} Inch`;
          }
          return (
            <Label
              key={flatVital.id}
              style={{ margin: '0.15em' }}
              //color={"teal"}
            >
              <div style={{ display: 'flex' }}>
                <div>
                  <a href={`https://loinc.org/${flatVital.code}`} target="_blank" rel="noopener noreferrer">
                    {flatVital.display}
                  </a>
                  {': '}
                  {flatVital.valueString?.length > 0 && <b>{flatVital.valueString}</b>}
                  {flatVital.quantity && (
                    <b>{heightInFtAndInch ? heightInFtAndInch : `${flatVital.quantity} ${flatVital.unit}`}</b>
                  )}
                  {flatVital.valueCode && (
                    <a
                      href={`https://icdlist.com/?t=icd10&s=${flatVital.valueCode}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {flatVital.valueCode}
                    </a>
                  )}

                  <div>{effectiveDate}</div>
                </div>

                {!props.sidebar && (
                  <>
                    <div style={{ width: 10 }} />
                    <Icon
                      style={{ float: 'right' }}
                      size="large"
                      name="delete"
                      onClick={() => handleDelete(flatVital.id)}
                    />
                  </>
                )}
              </div>
            </Label>
          );
        })}

        {!props.sidebar && (
          <Popup
            trigger={
              <Popup
                trigger={<Label as="a">+</Label>}
                content={
                  <Form onSubmit={handleSubmit}>
                    <Form.Group>
                      <Form.Input label="Create Blood Pressure Vital" placeholder="Enter Value" name="newobs" />
                      <Form.Button primary content="Add" />
                    </Form.Group>
                  </Form>
                }
                on="click"
                position="top right"
                open={popOpen}
                onOpen={handlePopOpen}
                onClose={handleClose}
              />
            }
            content={<Label as="a">Create New Blood Pressure Vital</Label>}
            on="hover"
            position="bottom right"
          />
        )}
      </>
    );
  } else {
    return <></>;
  }
}

export default Vitals;
